import { useState, useCallback, useEffect } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import apiUrl from "../libs/baseUrl";
import { errorHandeling } from "../errorHandeling";

// Consumes GET /notifications (paginated) plus the mark-read actions.
// Each notification item carries its payload under `data` (data.event_type,
// data.title_ar, data.body_ar, data.payload, data.action_url) alongside the
// top-level `id`, `read_at`, `created_at`.
export default function useNotifications({ page = 1, filters = {}, perPage = 15 } = {}) {
  const token = useSelector((state) => state.user.token);

  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(page);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const buildParams = useCallback(
    (pageNumber, activeFilters) => {
      const params = { page: pageNumber, per_page: perPage };
      // read: 'unread' | 'read'
      if (activeFilters.read) params.read = activeFilters.read;
      if (activeFilters.event_type) params.event_type = activeFilters.event_type;
      return params;
    },
    [perPage]
  );

  const fetchNotifications = useCallback(
    async (pageNumber = 1, activeFilters = {}) => {
      if (!token) return;

      setLoading(true);
      setError(null);

      try {
        const response = await axios.get(`${apiUrl}/notifications`, {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
          params: buildParams(pageNumber, activeFilters),
        });

        if (response.data?.status === "success") {
          const result = response.data.data;
          setData(result.data ?? []);
          setCurrentPage(result.current_page ?? 1);
          setTotalPages(result.last_page ?? 1);
        } else {
          setData([]);
          setError("فشل في جلب الإشعارات");
        }
      } catch (err) {
        setData([]);
        setError(errorHandeling(err));
      } finally {
        setLoading(false);
      }
    },
    [token, buildParams]
  );

  const markAsRead = useCallback(
    async (id) => {
      if (!token) return;
      try {
        await axios.post(
          `${apiUrl}/notifications/${id}/mark-read`,
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: "application/json",
            },
          }
        );
        // Optimistically flag the row as read.
        setData((prev) =>
          prev.map((n) =>
            n.id === id ? { ...n, read_at: n.read_at ?? new Date().toISOString() } : n
          )
        );
        return true;
      } catch (err) {
        return false;
      }
    },
    [token]
  );

  const markAllAsRead = useCallback(async () => {
    if (!token) return;
    try {
      await axios.post(
        `${apiUrl}/notifications/mark-all-read`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        }
      );
      const now = new Date().toISOString();
      setData((prev) => prev.map((n) => (n.read_at ? n : { ...n, read_at: now })));
      return true;
    } catch (err) {
      return false;
    }
  }, [token]);

  const filtersKey = JSON.stringify(filters ?? {});

  useEffect(() => {
    fetchNotifications(page, filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filtersKey, fetchNotifications]);

  return {
    data,
    currentPage,
    totalPages,
    isLoading: loading,
    error,
    refetch: fetchNotifications,
    markAsRead,
    markAllAsRead,
  };
}
